import type { FastifyInstance } from 'fastify'
import type { CollabController } from './collab.controller.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'

/**
 * Backward compatibility routes for /api/sharing
 * All methods delegated to unified CollabController
 * Note: Controllers handle response sending via ApiResponse utilities
 */
export function sharingRoutesCompat(fastify: FastifyInstance, controller: CollabController) {

  // Create share (auth required)
  fastify.post<{ Params: { planId: string }; Body: { privacy: 'public' | 'private'; password?: string; expiresAt?: string | number; allowComments?: boolean } }>(
    '/plans/:planId/share',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      return controller.createShare(request, reply, planId, request.body as any)
    }
  )

  // List shares for plan (auth required)
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/share',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      return controller.listShares(request, reply, planId)
    }
  )

  // Access share (public, optional password)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/access/:token',
    async (request, reply) => {
      const { token } = request.params
      return controller.accessShare(request, reply, token, request.body as any)
    }
  )

  // Public plan view (public, optional password)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/public/:token',
    async (request, reply) => {
      const { token } = request.params
      return controller.getPublicPlan(request, reply, token, request.body ?? {})
    }
  )

  // Join a shared plan (auth required)
  // This adds the user as a collaborator on the ORIGINAL plan (not a copy)
  fastify.post<{ Params: { token: string }; Body: { password?: string } }>(
    '/join/:token',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { token } = request.params
      return controller.joinPlan(request, reply, token, request.body ?? {})
    }
  )

  // Delete share (auth required)
  fastify.delete<{ Params: { shareId: string } }>(
    '/:shareId',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { shareId } = request.params
      return controller.deleteShare(request, reply, shareId)
    }
  )

  // Get comments (public)
  fastify.get<{ Params: { planId: string } }>(
    '/plans/:planId/comments',
    async (request, reply) => {
      const { planId } = request.params
      return controller.getComments(request, reply, planId)
    }
  )

  // Create comment (auth required)
  fastify.post<{ Params: { planId: string }; Body: { content: string; parentId?: string } }>(
    '/plans/:planId/comments',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { planId } = request.params
      return controller.createComment(request, reply, planId, request.body as any)
    }
  )

  // Delete comment (auth required)
  fastify.delete<{ Params: { commentId: string } }>(
    '/comments/:commentId',
    { preHandler: [authMiddleware] },
    async (request, reply) => {
      const { commentId } = request.params
      return controller.deleteComment(request, reply, commentId)
    }
  )
}
